import { updateAttributes } from "react-cognito";
import React, { useEffect, useState } from "react";
import DottedLoader from "../../DottedLoader";
import { Amplify, Auth } from "aws-amplify";
import AWS from "aws-sdk";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import awsConfig from "../../../../environments/env";
import ENVIRONMENT from "../../../../environments";
import axios from "axios";
import { submitToSalesForce, submitToHubSpot } from "./utils";


const OAuthCallback = ({ dispatch, history, cognito }) => {
  /**
   * OAuthCallback Component
   *
   * This component handles the OAuth authentication callback for Google sign-in using AWS Amplify.
   * It retrieves the authenticated user from Cognito, checks if the user exists in the backend,
   * and updates both the Redux state and react-cognito's authentication state accordingly.
   *
   * Workflow:
   * 1. Configures AWS Amplify.
   * 2. After federated SignIn, it redirects to OAuthCallback URL.
   * 3. Fetches the authenticated user from AWS Cognito.
   * 4. Retrieves the user's session and JWT token.
   * 5. Checks if the user already exists in the backend.
   * 6. If the user does not exist, registers them with a default profile.
   * 7. Stores the authentication token and username in localStorage.
   * 8. Updates the react-cognito attributes.
   * 9. Dispatches a Redux action to update the authentication state.
   * 10. Redirects the user to the home page upon successful login.
   * 12. Redirects the user to the login page in case of failure.
   */

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const urlParams = localStorage.getItem('urlParams') || '';
  const queryString = urlParams.includes('?') ? urlParams.split('?')[1] : '';
  const params = new URLSearchParams(queryString);
  const utmSource = params.get('utm_source');

  useEffect(() => {
    Amplify.configure(awsConfig);
    const fetchUser = async () => {
      setIsLoading(true);
      try {
        const user = await Auth.currentAuthenticatedUser();
        const session = await Auth.currentSession();
        const jwtToken = session.getIdToken().getJwtToken();

        const isUserExists = await axios.post(ENVIRONMENT.IS_USER_IN_DB, {
          email: user.attributes.email,
        });
        const username = user?.attributes?.email.split("@")[0];

        if (!isUserExists?.data?.is_existing_user) {
          const payload = {
            email: user.attributes.email,
            username: username,
            user_group: "customer",
            profile_name: `${user.attributes?.given_name} ${user.attributes?.family_name}`,
          };
          if (ENVIRONMENT.ENV == "production" && !user.attributes.email.includes("all3d")) {
            // submitToSalesForce(payload, { utmSource: utmSource });
            submitToHubSpot(payload, { utmSource: utmSource });
          }
          await axios.post(ENVIRONMENT.USER_SIGNUP_SIMPLE, payload);
        }

        localStorage.setItem("all3d_jwt_token", jwtToken);
        localStorage.setItem("username", username);

        dispatch({
          type: "GOOGLE_LOGIN",
          payload: {
            state: "LOGGED_IN",
            user: {
              username: username,
              authenticationFlowType: "USER_SRP_AUTH",
              signInUserSession: {
                idToken: {
                  jwtToken: jwtToken,
                  payload: {
                    sub: user?.attributes?.sub,
                    email: user?.attributes?.email,
                    email_verified: true,
                    first_name: user?.attributes?.given_name,
                    last_name: user?.attributes?.family_name,
                  },
                },
                accessToken: {
                  jwtToken: session.getAccessToken(),
                  payload: {
                    scope: "email profile openid",
                    iss: "https://accounts.google.com",
                  },
                },
              },
            },
            attributes: {
              email: user?.attributes?.email,
              email_verified: true,
              first_name: user?.attributes?.given_name,
              last_name: user?.attributes?.family_name,
              username: username,
              sub: user?.attributes?.sub,
            },
          },
        });

        // This sometimes fails for brand new federated signups because the underlying cognito user
        // (used by react-cognito policies) may not be ready yet. Don't block login/redirect on it.
        try {
          const action = await updateAttributes(
            user,
            {
              email: user?.attributes?.email,
            },
            {}
          );
          if (action) dispatch(action);
        } catch (e) {
          console.error("updateAttributes failed in OAuthCallback (non-blocking):", e);
        }

        setIsLoading(false);
        if (urlParams && decodeURIComponent(urlParams).includes("/select-subscription")) {
          history.replace(`${urlParams}`);
        } else {
          history.replace("/home");
        }
      } catch (err) {
        setError(
          "This email is registered with a password. Please log in using your credentials."
        );
        setIsLoading(false);
        setTimeout(() => history.push("/"), 3000);
      }
    };

    fetchUser();
  }, []);

  return (
    <div style={{ textAlign: "center", padding: "20px" }}>
      {isLoading ? (
        <DottedLoader />
      ) : error ? (
        <p style={{ color: "red", fontSize: "14px" }}>
          {error || "An unexpected error occurred. Please try again."}
        </p>
      ) : (
        <p>Processing authentication...</p>
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    cognito: state.cognito,
    rootReducerUser: state.rootReducer?.user,
  };
};

export default withRouter(connect(mapStateToProps)(OAuthCallback));
